import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { supabase } from '../lib/supabase';
import { errorMessage, formatTime } from '../lib/format';
import type { ClassSession, ClassType, Holiday } from '../lib/types';
import { PageHead } from '../components/AppLayout';
import { NoClassDayDialog } from '../components/NoClassDayDialog';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Session joined with its parent class's type (for color + label).
interface CalSession extends ClassSession {
  classes: { title: string | null; class_type_id: string } | null;
}

export function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [sessions, setSessions] = useState<CalSession[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [types, setTypes] = useState<Record<string, ClassType>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dayDialog, setDayDialog] = useState<Date | null>(null);

  const gridStart = useMemo(() => startOfWeek(startOfMonth(month)), [month]);
  const gridEnd = useMemo(() => endOfWeek(endOfMonth(month)), [month]);
  const days = useMemo(
    () => eachDayOfInterval({ start: gridStart, end: gridEnd }),
    [gridStart, gridEnd],
  );

  const reload = useCallback(async () => {
    const [sessRes, holRes, typeRes] = await Promise.all([
      supabase
        .from('class_sessions')
        .select('*, classes(title, class_type_id)')
        .gte('starts_at', gridStart.toISOString())
        .lte('starts_at', gridEnd.toISOString())
        .order('starts_at', { ascending: true }),
      supabase
        .from('holidays')
        .select('*')
        .gte('holiday_date', format(gridStart, 'yyyy-MM-dd'))
        .lte('holiday_date', format(gridEnd, 'yyyy-MM-dd')),
      supabase.from('class_types').select('*'),
    ]);
    const firstErr = sessRes.error || holRes.error || typeRes.error;
    if (firstErr) setError(errorMessage(firstErr));
    else setError(null);
    setSessions((sessRes.data ?? []) as CalSession[]);
    setHolidays((holRes.data ?? []) as Holiday[]);
    const map: Record<string, ClassType> = {};
    for (const t of (typeRes.data ?? []) as ClassType[]) map[t.id] = t;
    setTypes(map);
    setLoading(false);
  }, [gridStart, gridEnd]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void reload().then(() => {
      if (!active) return;
    });
    return () => {
      active = false;
    };
  }, [reload]);

  const sessionsByDay = useMemo(() => {
    const m = new Map<string, CalSession[]>();
    for (const s of sessions) {
      const key = format(parseISO(s.starts_at), 'yyyy-MM-dd');
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [sessions]);

  const holidayByDay = useMemo(() => {
    const m = new Map<string, Holiday>();
    for (const h of holidays) m.set(h.holiday_date, h);
    return m;
  }, [holidays]);

  // Mobile agenda: this month's sessions from today forward (the grid
  // shows events as dots on small screens, so this is the readable list).
  const agenda = useMemo(() => {
    const cutoff = startOfDay(new Date());
    return sessions.filter((s) => parseISO(s.starts_at) >= cutoff);
  }, [sessions]);

  // Types actually visible this month → the legend.
  const legendTypes = useMemo(() => {
    const seen = new Map<string, ClassType>();
    for (const s of sessions) {
      const t = s.classes ? types[s.classes.class_type_id] : undefined;
      if (t) seen.set(t.id, t);
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [sessions, types]);

  return (
    <>
      <PageHead
        title="Calendar"
        subtitle="Your full schedule. Click a class to manage it, or a day to mark it class-free."
        action={
          <div className="row">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setMonth((m) => subMonths(m, 1))}
            >
              ← Prev
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setMonth(startOfMonth(new Date()))}
            >
              Today
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setMonth((m) => addMonths(m, 1))}
            >
              Next →
            </button>
          </div>
        }
      />

      <h2 style={{ marginBottom: '0.8rem' }}>{format(month, 'MMMM yyyy')}</h2>
      {error && <p className="error-text">{error}</p>}

      <div className="cal-grid">
        {DOW.map((d) => (
          <div key={d} className="cal-dow">
            {d}
          </div>
        ))}
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const daySessions = sessionsByDay.get(key) ?? [];
          const holiday = holidayByDay.get(key);
          const classes = [
            'cal-cell',
            isSameMonth(day, month) ? '' : 'out',
            isSameDay(day, new Date()) ? 'today' : '',
            holiday ? 'holiday' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={key}
              className={classes}
              onClick={() => setDayDialog(day)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setDayDialog(day);
              }}
              aria-label={`${format(day, 'MMMM d')}${holiday ? ` — ${holiday.label}` : ''}`}
            >
              <span className="cal-daynum">{format(day, 'd')}</span>
              <span className="cal-add" aria-hidden>
                {holiday ? '' : '+'}
              </span>
              {holiday && (
                <span className="cal-holiday-tag">
                  🚫<span className="cal-holiday-label"> {holiday.label}</span>
                </span>
              )}
              {daySessions.map((s) => {
                const type = s.classes ? types[s.classes.class_type_id] : undefined;
                const label = s.classes?.title || type?.name || 'Class';
                return (
                  <span
                    key={s.id}
                    className="cal-event"
                    style={{
                      background: type?.color ?? 'var(--brand)',
                      opacity: s.status === 'cancelled' ? 0.45 : 1,
                      textDecoration: s.status === 'cancelled' ? 'line-through' : undefined,
                    }}
                    title={`${label} · ${formatTime(s.starts_at)}${s.status === 'cancelled' ? ' (cancelled)' : ''} — click to manage`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/classes/${s.class_id}`);
                    }}
                  >
                    {formatTime(s.starts_at)} {label}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>

      {(legendTypes.length > 0 || holidays.length > 0) && (
        <div className="cal-legend">
          {legendTypes.map((t) => (
            <span className="item" key={t.id}>
              <span className="dot" style={{ background: t.color }} /> {t.name}
            </span>
          ))}
          {holidays.length > 0 && <span className="item">🚫 No-class day</span>}
        </div>
      )}

      {!loading && sessions.length === 0 && (
        <p className="muted small" style={{ marginTop: '1rem' }}>
          No classes scheduled this month yet. Schedule one from the <strong>Classes</strong> tab —
          it'll appear here automatically.
        </p>
      )}

      {/* Phone-only readable list of what's coming up this month. */}
      <div className="cal-agenda">
        <h3>Coming up</h3>
        {agenda.length === 0 ? (
          <p className="muted small">No upcoming classes this month.</p>
        ) : (
          agenda.map((s) => {
            const type = s.classes ? types[s.classes.class_type_id] : undefined;
            const label = s.classes?.title || type?.name || 'Class';
            return (
              <button
                key={s.id}
                className={`agenda-item${s.status === 'cancelled' ? ' cancelled' : ''}`}
                onClick={() => navigate(`/app/classes/${s.class_id}`)}
              >
                <span className="dot" style={{ background: type?.color ?? 'var(--brand)' }} />
                <span className="agenda-when">{format(parseISO(s.starts_at), 'EEE MMM d')}</span>
                <span className="agenda-time">{formatTime(s.starts_at)}</span>
                <span className="agenda-label">{label}</span>
              </button>
            );
          })
        )}
      </div>

      {dayDialog && (
        <NoClassDayDialog
          fixedDay={dayDialog}
          holiday={holidayByDay.get(format(dayDialog, 'yyyy-MM-dd')) ?? null}
          onClose={() => setDayDialog(null)}
          onChanged={() => {
            setDayDialog(null);
            void reload();
          }}
        />
      )}
    </>
  );
}
